/**
 * pxtxt API Client
 *
 * Wrapper for interview API calls with auth.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'https://pxtxt.itsmin.workers.dev';

interface ApiOptions {
	method?: 'GET' | 'POST' | 'PATCH';
	body?: unknown;
}

/** API error with structured metadata from the server response. */
export class ApiError extends Error {
	upgradeRequired: boolean;
	constructor(message: string, { upgradeRequired = false }: { upgradeRequired?: boolean } = {}) {
		super(message);
		this.name = 'ApiError';
		this.upgradeRequired = upgradeRequired;
	}
}

async function apiCall<T>(token: string, endpoint: string, options: ApiOptions = {}): Promise<T> {
	const { method = 'GET', body } = options;

	const response = await fetch(`${API_BASE}${endpoint}`, {
		method,
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: body ? JSON.stringify(body) : undefined,
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({ error: 'Request failed' }));
		throw new ApiError(error.error || error.message || 'Request failed', {
			upgradeRequired: !!error.upgradeRequired,
		});
	}

	return response.json();
}

// Profile canonical data (lightweight frontend type)
export interface ProfileCanonicalWorkHistory {
	company: string;
	title?: string;
	period?: string;
	highlights?: string[];
	evidence_assets?: { url: string; label: string; type?: string }[];
	beta_enrichment?: { verified?: boolean; entity_key?: string; entity_type?: string };
}

export interface ProfileCanonical {
	identity?: {
		name?: { full?: string; preferred?: string; pronunciation?: string };
		positioning?: { title?: string; tagline?: string; focus_areas?: string[] };
		pronouns?: string;
	};
	experience?: {
		work_history?: ProfileCanonicalWorkHistory[];
	};
	projects?: {
		portfolio_projects?: { id: string; name: string; company: string }[];
	};
	skills?: {
		capabilities?: { name: string }[];
		domains?: { name: string }[];
	};
}

// Types
interface UserInfo {
	id: string;
	email: string;
	tier: 'free' | 'pro';
	emailVerified: boolean;
	interviewsRemaining: number;
	canStartInterview: boolean;
	activeSessionId: string | null;
}

export interface SessionContractInfo {
	budget: number;
	exchangeCount: number;
	phase: 'working' | 'consolidation' | 'wrap_up' | 'grace' | 'backstop';
	exchangesRemaining: number;
}

export interface SessionSummaryData {
	objective: string;
	covered: string[];
	captured: string[];
	remaining: string[];
	suggestedNext: string;
	generatedAt: number;
}

export type ReflectionDecisionType =
	| 'interview_processing'
	| 'role_fit'
	| 'pivot_consideration'
	| 'comp_review'
	| 'relationship'
	| 'learning'
	| 'other';

export type ReflectionEntityRef =
	| { type: 'company'; name: string; canonicalId?: string }
	| { type: 'project'; name: string; canonicalId?: string }
	| { type: 'person'; name: string };

export interface ReflectionMetadataData {
	decisionType: ReflectionDecisionType;
	decisionTypeOther?: string;
	entitiesReferenced: ReflectionEntityRef[];
	threadLinks: string[];
	summaryLine?: string;
	generatedAt: number;
	generationCount: number;
}

interface SessionSummary {
	id: string;
	type: string;
	mode?: 'canonical' | 'private';
	status: string;
	startedAt: number;
	lastActiveAt: number;
	messageCount: number;
	pendingClaimCount: number;
	sessionSummary?: SessionSummaryData | null;
	reflectionMetadata?: ReflectionMetadataData | null;
}

interface Message {
	role: 'user' | 'assistant' | 'system';
	content: string;
	timestamp: number;
}

interface Claim {
	id: string;
	section: string;
	field: string;
	value: unknown;
	confidence: 'stated' | 'inferred';
	timestamp: number;
	agentReasoning?: string;
	narrative?: string;
}

interface Gap {
	id: string;
	section: string;
	field?: string;
	reason: string;
	priority: 'high' | 'medium' | 'low';
	status: 'pending' | 'explored' | 'resolved';
}

export interface SessionFocus {
	type: 'new_content' | 'refine' | 'guided';
	topic?: string;
	target?: string;
	targetSection?: string;
}

// Application types
export type ApplicationStatus =
	| 'considering'
	| 'applied'
	| 'interviewing'
	| 'feedback_received'
	| 'rejected'
	| 'accepted'
	| 'declined'
	| 'withdrawn';

export interface ApplicationRecord {
	id: string;
	userId: string;
	sessionId: string;
	company: string;
	role: string | null;
	url: string | null;
	status: ApplicationStatus;
	notes: string | null;
	createdAt: number;
	updatedAt: number;
}

// Workspace types
export interface WorkspaceNote {
	id: string;
	sessionId?: string;
	type: 'source' | 'attribution' | 'action_item' | 'pinned';
	title: string;
	content: Record<string, unknown>;
	messageIndex: number | null;
	createdAt: number;
}

interface SessionDetail {
	session: {
		id: string;
		type: string;
		mode?: 'canonical' | 'private';
		status: string;
		sessionFocus?: SessionFocus | null;
		startedAt: number;
		lastActiveAt: number;
		areasExplored: string[];
	};
	messages: Message[];
	gaps: {
		remaining: Gap[];
		explored: Gap[];
		resolved: Gap[];
	};
	claims: {
		pending: Claim[];
		approved: Claim[];
		rejectedCount: number;
	};
	contract?: SessionContractInfo;
	sessionSummary?: SessionSummaryData;
	compiledView?: import('../components/ProfileNotes').CompiledProfileView;
	workspaceNotes?: WorkspaceNote[];
}

interface StartInterviewResult {
	sessionId: string;
	opening: string | null;
	gaps: {
		total: number;
		summary: string;
		current: Gap | null;
	};
	interviewsRemaining: number;
}

interface SendMessageResult {
	message: string;
	extractedClaims: Claim[];
	suggestedNextGap?: string;
	claimCounts: {
		pending: number;
		approved: number;
		rejected: number;
	};
	profileDataReady?: boolean;
	contract?: SessionContractInfo;
	sessionCompleted?: boolean;
	sessionSummary?: SessionSummaryData;
	extractionPending?: boolean;
}

interface SessionClaimsResult {
	claims: Claim[];
	counts: {
		pending: number;
		approved: number;
		rejected: number;
	};
	since: number;
}

// Professional Narrative types
export interface NarrativeSection {
	type: 'identity' | 'arc' | 'role' | 'expertise' | 'perspective';
	heading: string;
	content: string;
	company?: string;
}

export interface ProfessionalNarrative {
	generatedAt: number;
	format: 'pxtxt-narrative-v1';
	depth: 'baseline' | 'full';
	sections: NarrativeSection[];
	provenance: {
		claimCount: number;
		sessionCount: number;
	};
}

export type NarrativeResponse =
	| { id: string; narrative: ProfessionalNarrative }
	| { narrative: null; canGenerate: boolean; claimCount: number };

export interface ProfessionalResume {
	generatedAt: number;
	format: 'pxtxt-resume-v1';
	resumeFormat: 'ats' | 'narrative';
	header: {
		name?: string;
		title?: string;
		email?: string;
		linkedin?: string;
		portfolio?: string;
	};
	summary?: string;
	experience: Array<{
		company: string;
		title?: string;
		period?: string;
		description?: string;
		highlights: string[];
	}>;
	skills: {
		capabilities: string[];
		tools: string[];
		domains: string[];
	};
	projects: Array<{
		name: string;
		company?: string;
		summary?: string;
	}>;
	narrativeReadiness?: {
		rolesWithNarratives: number;
		totalRoles: number;
	};
}

// API methods
export const api = {
	async getMe(token: string): Promise<UserInfo> {
		return apiCall<UserInfo>(token, '/api/auth/me');
	},

	async listSessions(token: string): Promise<{ sessions: SessionSummary[] }> {
		return apiCall<{ sessions: SessionSummary[] }>(token, '/api/interview/sessions');
	},

	async getSession(token: string, sessionId: string): Promise<SessionDetail> {
		return apiCall<SessionDetail>(token, `/api/interview/session/${sessionId}`);
	},

	async startInterview(
		token: string,
		options: { type: string; mode?: 'canonical' | 'private'; intent?: string },
	): Promise<StartInterviewResult> {
		return apiCall<StartInterviewResult>(token, '/api/interview/start', {
			method: 'POST',
			body: options,
		});
	},

	async sendMessage(token: string, data: { sessionId: string; message: string; currentGapId?: string }): Promise<SendMessageResult> {
		return apiCall<SendMessageResult>(token, '/api/interview/message', {
			method: 'POST',
			body: data,
		});
	},

	async getSessionClaims(token: string, sessionId: string, since?: number): Promise<SessionClaimsResult> {
		const params = since ? `?since=${since}` : '';
		return apiCall<SessionClaimsResult>(token, `/api/interview/session/${sessionId}/claims${params}`);
	},

	async claimAction(token: string, claimId: string, data: { action: 'approve' | 'reject'; editedValue?: unknown }): Promise<{ success: boolean }> {
		return apiCall<{ success: boolean }>(token, `/api/interview/claim/${claimId}`, {
			method: 'POST',
			body: data,
		});
	},

	async bulkClaimAction(token: string, data: { action: 'approve' | 'reject'; claimIds: string[] }): Promise<{ success: boolean; count: number }> {
		return apiCall<{ success: boolean; count: number }>(token, '/api/interview/claims/bulk', {
			method: 'POST',
			body: data,
		});
	},

	async exportSession(token: string, sessionId: string): Promise<unknown> {
		return apiCall<unknown>(token, '/api/interview/export', {
			method: 'POST',
			body: { sessionId },
		});
	},

	async exportAllData(token: string): Promise<Blob> {
		const response = await fetch(`${API_BASE}/api/interview/export-all`, {
			headers: {
				'Authorization': `Bearer ${token}`,
			},
		});
		if (!response.ok) {
			const error = await response.json().catch(() => ({ error: 'Export failed' }));
			throw new Error(error.error || 'Export failed');
		}
		return response.blob();
	},

	async setSessionFocus(token: string, sessionId: string, focus: SessionFocus): Promise<{ sessionFocus: SessionFocus; opening: string }> {
		return apiCall(token, `/api/interview/session/${sessionId}/focus`, {
			method: 'POST',
			body: { focus },
		});
	},

	async completeSession(token: string, sessionId: string): Promise<{ success: boolean; summary?: SessionSummaryData }> {
		return apiCall<{ success: boolean; summary?: SessionSummaryData }>(token, `/api/interview/session/${sessionId}/complete`, {
			method: 'POST',
		});
	},

	async abandonSession(token: string, sessionId: string): Promise<{ success: boolean }> {
		return apiCall<{ success: boolean }>(token, `/api/interview/session/${sessionId}/abandon`, {
			method: 'POST',
		});
	},

	async createCheckoutSession(token: string, plan: 'monthly' | 'annual' = 'monthly'): Promise<{ url: string }> {
		return apiCall<{ url: string }>(token, '/api/stripe/checkout', {
			method: 'POST',
			body: { plan },
		});
	},

	async createBillingPortal(token: string): Promise<{ url: string }> {
		return apiCall<{ url: string }>(token, '/api/stripe/portal', { method: 'POST' });
	},

	async getResume(token: string, format?: 'ats' | 'narrative'): Promise<ProfessionalResume> {
		const query = format ? `?format=${format}` : '';
		return apiCall<ProfessionalResume>(token, `/api/interview/resume${query}`);
	},

	async getCanonical(token: string): Promise<{
		canonical: ProfileCanonical | null;
		stats: { totalClaims: number; sessionCount: number; lastUpdated: number };
	}> {
		return apiCall(token, '/api/interview/profile/canonical');
	},

	async updateIdentitySettings(
		token: string,
		fields: { preferred?: string; pronunciation?: string; pronouns?: string },
	): Promise<{ identity: ProfileCanonical['identity'] }> {
		return apiCall(token, '/api/interview/profile/identity-settings', {
			method: 'POST',
			body: fields,
		});
	},

	async getProfileClaims(token: string): Promise<{ claims: Array<{ id: string; section: string; field: string; value: unknown; confidence: 'stated' | 'inferred'; timestamp: number }> }> {
		return apiCall(token, '/api/interview/profile/claims');
	},

	async getProfileView(token: string): Promise<import('../components/ProfileNotes').CompiledProfileView> {
		return apiCall(token, '/api/interview/profile/view');
	},

	async getReports(token: string): Promise<{ reports: EngagementReport[] }> {
		return apiCall<{ reports: EngagementReport[] }>(token, '/api/interview/reports');
	},

	async getAdminDashboard(token: string): Promise<AdminDashboardData> {
		return apiCall<AdminDashboardData>(token, '/api/admin/dashboard');
	},

	async getAdminUsers(token: string, params?: { search?: string; tier?: string; page?: number; limit?: number }): Promise<AdminUserList> {
		const qs = new URLSearchParams();
		if (params?.search) qs.set('search', params.search);
		if (params?.tier) qs.set('tier', params.tier);
		if (params?.page) qs.set('page', String(params.page));
		if (params?.limit) qs.set('limit', String(params.limit));
		const query = qs.toString();
		return apiCall<AdminUserList>(token, `/api/admin/users${query ? `?${query}` : ''}`);
	},

	async updateAdminUserTier(token: string, userId: string, tier: 'free' | 'pro'): Promise<{ success: boolean }> {
		return apiCall<{ success: boolean }>(token, `/api/admin/user/${userId}/tier`, {
			method: 'POST',
			body: { tier },
		});
	},

	async grantAdminUserTrial(token: string, userId: string, days: number): Promise<{ success: boolean; proExpiresAt: number; days: number }> {
		return apiCall<{ success: boolean; proExpiresAt: number; days: number }>(token, `/api/admin/user/${userId}/trial`, {
			method: 'POST',
			body: { days },
		});
	},

	async getSessionWorkspace(token: string, sessionId: string): Promise<{ notes: WorkspaceNote[] }> {
		return apiCall<{ notes: WorkspaceNote[] }>(token, `/api/interview/session/${sessionId}/workspace`);
	},

	async getWorkspace(token: string): Promise<{ notes: WorkspaceNote[] }> {
		return apiCall<{ notes: WorkspaceNote[] }>(token, '/api/interview/workspace');
	},

	// Application tracking methods

	async getApplications(token: string): Promise<{ applications: ApplicationRecord[] }> {
		return apiCall<{ applications: ApplicationRecord[] }>(token, '/api/interview/applications');
	},

	async updateApplication(token: string, id: string, updates: { status?: ApplicationStatus; notes?: string; url?: string }): Promise<{ updated: boolean }> {
		return apiCall<{ updated: boolean }>(token, `/api/interview/applications/${id}`, {
			method: 'PATCH',
			body: updates,
		});
	},

	// Narrative methods

	async generateNarrative(token: string): Promise<{ id: string; narrative: ProfessionalNarrative }> {
		return apiCall(token, '/api/interview/narrative/generate', { method: 'POST' });
	},

	async getNarrative(token: string): Promise<NarrativeResponse> {
		return apiCall<NarrativeResponse>(token, '/api/interview/narrative');
	},
};

// Engagement Report type
export interface EngagementReport {
	id: string;
	tenantSlug: string;
	startedAt: number;
	lastActiveAt: number;
	messageCount: number;
	visitorRole: string | null;
	visitorCompany: string | null;
	visitorQuality: string;
	topicsExplored: string[];
	depthIndicators: string[];
	nodesNavigated: string[];
	contactRequested: boolean;
	qualificationReasoning: string | null;
	createdAt: number;
	debrief: EngagementDebrief | null;
	debriefGeneratedAt: number | null;
}

// Structured debrief generated by agent after substantive conversations
export interface EngagementDebrief {
	visitorSummary: string;
	keyInterests: string[];
	classification: {
		role: string;
		quality: string;
		reasoning: string;
	};
	recommendedAction: string;
	coverageGaps?: string[];
}

// Admin Dashboard types
export interface AdminDashboardData {
	overview: {
		totalUsers: number;
		proUsers: number;
		verifiedUsers: number;
		totalEngagements: number;
		totalProfiles: number;
		publishedProfiles: number;
		chatEnabledProfiles: number;
		totalNarratives: number;
		baselineNarratives: number;
		fullNarratives: number;
	};
	engagements: {
		total: number;
		mtd: number;
		last7d: number;
		avgMessageCount: number;
		contactRequestRate: number;
		debriefCoverage: number;
		qualityDistribution: Record<string, number>;
		roleDistribution: Record<string, number>;
		recent: Array<{
			id: string;
			tenantSlug: string;
			startedAt: number;
			lastActiveAt: number;
			messageCount: number;
			visitorRole: string | null;
			visitorQuality: string;
			contactRequested: boolean;
			hasDebrief: boolean;
		}>;
	};
	users: {
		total: number;
		tierDistribution: Record<string, number>;
		emailVerificationRate: number;
		interviewSessions: Record<string, number>;
		claims: Record<string, number>;
	};
	costs: {
		totalEstimated: number;
		mtdEstimated: number;
		avgPerEngagement: number;
		avgPerSession: number;
		breakdown: {
			representation: number;
			debriefs: number;
			extraction: number;
			coaching: number;
			artifacts: number;
		};
	};
	alerts: Array<{
		type: 'info' | 'warning' | 'error';
		category: string;
		message: string;
		count?: number;
	}>;
}

// Admin user management
export interface AdminUser {
	id: string;
	email: string;
	tier: 'free' | 'pro';
	proExpiresAt: number | null;
	emailVerified: boolean;
	stripeCustomerId: string | null;
	createdAt: number;
	updatedAt: number;
	sessionCount: number;
	approvedClaims: number;
	hasPublicProfile: boolean;
	profileSlug: string | null;
	estimatedCost: number;
}

export interface AdminUserList {
	users: AdminUser[];
	total: number;
	page: number;
	limit: number;
}

